import { Weekday } from "@/data/model/Enums";
import type { IScheduleShift } from "@/data/model/OfferSchedule";
import { DateTime } from "@/data/util/DateTime";

import { i18n } from "@/portal/Trans";
import { Shift } from "@/portal/elements/Shift";

const SECONDS_IN_A_DAY = 24 * 60 * 60;

export class OfferSchedulesEditor {
    private readonly $element: JQuery;
    private readonly $shifts: JQuery;

    constructor() {
        this.$element = $('<div class="offerscheduleseditor" />');

        const optionRow = $('<div class="row optrow" />').appendTo(this.$element);
        optionRow.append(
            '<label class="col-xl-3 col-lg-3 col-form-label text-right">' +
                i18n("general.entities.shifts") +
                ' <span class="mandatory">*</span></label>'
        );
        this.$shifts = $('<div class="col-lg-9 col-xl-6 shifts" />').appendTo(optionRow);

        const addRow = $('<div class="row addrow" />').appendTo(this.$element);
        const addBtn = $(
            '<div class="col-xl-9 pull-right"><button class="btn pull-right btn-success"><i class="la la-plus"></i> ' +
                i18n("general.entities.shift") +
                " </button></div>"
        )
            .appendTo(addRow)
            .find("> .btn");
        addBtn.click((e) => {
            e.preventDefault();
            this.addShift();
            this.checkNoData();
        });

        this.checkNoData();
    }

    public setValue(shifts?: Array<IScheduleShift>): OfferSchedulesEditor {
        this.$shifts.find("> .shift").each((i, e) => {
            $(e).data("ctrl").destroy();
        });

        if (shifts === undefined || shifts.length === 0) {
            this.addShift();
            this.checkNoData();
            return this;
        }

        for (const i in shifts) this.addShift(shifts[i]);

        this.checkNoData();

        return this;
    }

    public getValue(): Array<IScheduleShift> {
        const val = new Array<IScheduleShift>();

        this.$shifts.find("> .shift").each((i, e) => {
            val.push($(e).data("ctrl").getValue());
        });

        return val;
    }

    public validate(): boolean {
        let hasError = false;
        const vals = new Array<{ $el: JQuery; data: IScheduleShift }>();

        this.$element.find(".is-invalid").removeClass("is-invalid");

        this.$shifts.find("> .shift").each((i, e) => {
            const validation = $(e).data("ctrl").validateOrValue();
            if (validation === false) hasError = true;
            else vals.push({ $el: $(e), data: validation });
        });

        if (hasError) return false;

        if (vals.length === 0) {
            this.$element.addClass("is-invalid");
            return false;
        }

        //check all values for overlap
        const weeklyOccupation = new Array<{ startSecond: number; endSecond: number; weekday: Weekday }>();
        for (const i in vals) {
            const val = vals[i];
            let forWeekdays = val.data.weekdays;
            const startSeconds = val.data.wholeDay
                ? 0
                : val.data.from === undefined
                  ? 0
                  : DateTime.secondsOfTimeSpan(val.data.from);
            const endSeconds = val.data.wholeDay
                ? SECONDS_IN_A_DAY - 1
                : val.data.until === undefined
                  ? SECONDS_IN_A_DAY - 1
                  : DateTime.secondsOfTimeSpan(val.data.until);

            if (forWeekdays.length === 0) {
                forWeekdays = [
                    Weekday.Monday,
                    Weekday.Tuesday,
                    Weekday.Wednesday,
                    Weekday.Thursday,
                    Weekday.Friday,
                    Weekday.Saturday,
                    Weekday.Sunday,
                ];
            }

            for (const wi in forWeekdays) {
                const weekday = forWeekdays[wi];

                //check for existing occupation
                if (
                    weeklyOccupation.find(
                        (c) =>
                            c.weekday == weekday &&
                            DateTime.overlaps(
                                { start: c.startSecond, end: c.endSecond },
                                { start: startSeconds, end: endSeconds }
                            )
                    ) !== undefined
                ) {
                    val.$el.addClass("is-invalid");
                    return false;
                }

                weeklyOccupation.push({ weekday: weekday, startSecond: startSeconds, endSecond: endSeconds });
            }
        }

        return true;
    }

    public addShift(shift?: IScheduleShift) {
        const s = new Shift(shift).appendTo(this.$shifts);
        s.onRemoved = () => {
            this.checkNoData();
        };

        s.onClone = (shift) => {
            this.addShift(shift);
            this.checkNoData();
        };
    }

    private checkNoData() {
        const cont = this.$shifts.find("> .nodata");

        if (this.$shifts.find("> .shift").length === 0) {
            if (cont.length === 0)
                this.$shifts.append('<div class="nodata">' + i18n("elements.offerSchedulesEditor.nodata") + "</div>");
        } else {
            if (cont.length !== 0) cont.remove();
        }
    }

    public renderTo($element: JQuery): OfferSchedulesEditor {
        this.$element.appendTo($element);

        return this;
    }

    public replaceWith($element: JQuery): OfferSchedulesEditor {
        $element.replaceWith(this.$element);

        return this;
    }
}
